"use client";
import {
  useMemo,
  useCallback,
  Fragment,
  useRef,
  useEffect,
  useState,
} from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ScrollArea } from "@/components/ui/scroll-area";
import Link from "next/link";
import { motion } from "framer-motion";
import { Loader, LoaderCircle, RefreshCcw, Trash2 } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useParams, useRouter } from "next/navigation";
import {
  deleteChatById,
  getChatList,
  getLatestChat,
} from "@/lib/api-client";
import DeleteConfirmation from "./DeleteConfirmation";
import { useToggle } from "@/hooks/useToggle";
import toast from "react-hot-toast";
import useServerAction from "@/hooks/useServerAction";
import useChat from "@/hooks/useChat";
import { useDispatch } from "react-redux";
import { resetAllChat } from "@/store/featureSlices/chatSlice";
import { resetChats } from "@/store/featureSlices/chatSlice";
import { Skeleton } from "@/components/ui/skeleton";

interface Chat {
  chat_id: string;
  updated_at: string;
  chat_history: { description: string }[];
}

interface ChatHistoryProps {
  initialChats: Chat[];
  refetch: () => void;
  isLoading: boolean;
}

const ChatHistoryList: React.FC<ChatHistoryProps> = ({
  initialChats,
  refetch,
  isLoading,
}) => {
  const { isToggled, handleOpen, handleClose } = useToggle();

  const router = useRouter();

  const { chat_id } = useParams();

  const { handleResetAllChat } = useChat();

  const selectChatRef = useRef<string | null>(null);

  const {
    mutate: deleteChatMutation,
    isPending,
    isSuccess,
  } = useMutation({
    mutationFn: async (chatId: string) => await deleteChatById(chatId),

    onSuccess: async (chatId) => {
      refetch();
    },
    onSettled: () => {
      if (selectChatRef.current === chat_id) {
        handleResetAllChat();
        router.push("/chatbot");
      }
      selectChatRef.current = null;
    },
  });

  const handleDeleteChat = () => {
    if (selectChatRef.current) {
      deleteChatMutation(selectChatRef.current);
    }
  };

  const handleDeleteChatConfirmation = (chatId: string) => {
    selectChatRef.current = chatId;
    handleOpen();
  };

  const chatItems = useMemo(() => {
    if (!initialChats || !Array.isArray(initialChats)) return [];

    return initialChats
      .sort(
        (a: any, b: any) =>
          new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
      )
      .map((item: any) => ({
        ...item,
        hasHistory:
          Array.isArray(item?.chat_history) && item?.chat_history?.length > 0,
      }));
  }, [initialChats]);

  const hasChats = Array.isArray(chatItems) && chatItems?.length > 0;

  useEffect(() => {
    if (!hasChats) {
      handleResetAllChat();
      router.push("/chatbot");
    }
  }, [hasChats, handleResetAllChat, router]);

  // if (!hasChats) {
  //   return (
  //     <div className="flex-1 flex flex-col gap-4 ">
  //       <div className="flex items-center justify-between w-full border-b py-2 pr-2">
  //         <span className="text-stone-950 dark:text-white text-base font-semibold">
  //           Previous Chats
  //         </span>
  //         <div>
  //           <span onClick={() => refetch()} className="cursor-pointer">
  //             <RefreshCcw size={18} />
  //           </span>
  //         </div>
  //       </div>
  //       <p className="flex-1 text-center">No chats available.</p>
  //     </div>
  //   );
  // }

  return (
    <Fragment>
      <div className="flex-1 flex flex-col gap-4 ">
        <div className="flex items-center justify-between w-full border-b py-2 pr-2">
          <span className="text-stone-950 dark:text-white text-xs lg:text-base font-semibold">
            Previous Chats
          </span>
          <div>
            <span onClick={() => refetch()} className="cursor-pointer">
              <RefreshCcw size={18} />
            </span>
          </div>
        </div>
        {isLoading ? (
          <ChatHistorySkeleton />
        ) : (
          <ScrollArea className="h-[450px] overflow-y-auto">
            <div className="flex flex-col gap-1">
              {hasChats &&
                chatItems.map((item: any, index: number) => {
                  const isDeleting =
                    isPending && selectChatRef.current === item.chat_id;

                  return (
                    <div
                      className="flex gap-2 items-center justify-between w-full cursor-pointer"
                      key={`${item.chat_id}-${index}`}
                    >
                      <Link
                        href={`/chatbot/${item.chat_id}`}
                        className="flex-1"
                      >
                        <motion.div
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          whileTap={{ scale: 0.93 }}
                          className={`flex flex-col gap-1 flex-1 py-2 rounded-md p-1 `}
                        >
                          <p className="text-stone-950 dark:text-zinc-100 text-sm font-medium text-wrap line-clamp-2">
                            {item.hasHistory
                              ? item?.chat_history?.[0]?.description
                              : "New Chat"}
                          </p>
                        </motion.div>
                      </Link>
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        whileTap={{ scale: 0.87 }}
                        className="pr-4"
                        onClick={() =>
                          handleDeleteChatConfirmation(item.chat_id)
                        }
                      >
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              {isDeleting ? (
                                <span className="text-orange-500 animate-spin">
                                  <Loader size={16} />
                                </span>
                              ) : (
                                <span className="text-rose-500">
                                  <Trash2 size={16} />
                                </span>
                              )}
                            </TooltipTrigger>
                            <TooltipContent>Delete Chat</TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </motion.div>
                    </div>
                  );
                })}
            </div>
          </ScrollArea>
        )}
      </div>

      <DeleteConfirmation
        isOpen={isToggled}
        deleteFn={handleDeleteChat}
        onClose={handleClose}
      />
    </Fragment>
  );
};

export default ChatHistoryList;

const ChatHistorySkeleton = () => {
  return (
    <div className="flex-1 flex flex-col gap-2 w-full h-full overflow-hidden relative">
      {[...Array(8)].map((_, index) => (
        <div
          key={index}
          className="flex items-center gap-2 justify-between w-full h-10"
        >
          <Skeleton className="flex-1 h-10 bg-gray-200" />
          <Skeleton className="w-5 h-5 bg-gray-200" />
        </div>
      ))}
    </div>
  );
};
